import { useTranslation } from 'i18n';
import { addError, logger } from 'monitor';
import { Button, CenteredLoader, ConfirmationButton, Instruction, List, Typography } from 'ui';

import { useAssignLoadToInspection } from '../../hooks/useAssignLoadToInspection/useAssignLoadToInspection';
import { useInspectionLoadByAssignment } from '../../hooks/useInspectionLoadByAssignment/useInspectionLoadByAssignment';
import { LoadListItem } from '../LoadListItem/LoadListItem';

export const InspectionLoadAssigner = ({
  inspectionId,
  dealId,
  inspectionSlug,
}: {
  inspectionId: number;
  dealId: number;
  inspectionSlug: string;
}) => {
  const { t } = useTranslation('orders');
  const { assign, reassign, unassign } = useAssignLoadToInspection();

  const { assignedLoads, unassignedLoads, otherInspectionsLoads, isLoading } = useInspectionLoadByAssignment(
    inspectionId,
    dealId
  );

  if (isLoading) {
    return <CenteredLoader />;
  }

  if (!assignedLoads && !unassignedLoads && !otherInspectionsLoads) {
    logger.warn(`No loads found for deal ${dealId}`, {
      dealId,
      inspectionId,
    });
    return <Typography>{t('inspectionLoad.assigner.states.noLoadsForSelectedDeal')}</Typography>;
  }

  return (
    <div>
      <Typography variant="h6">{t('inspectionLoad.assigner.sections.assignedLoads')}</Typography>
      {assignedLoads?.length ? (
        <List disablePadding>
          {assignedLoads.map((load) => (
            <LoadListItem key={load.id} load={load}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  if (!load.inspection_load_id) {
                    addError(Error('inspection_load_id missing during unassign'), load);
                    return;
                  }
                  unassign(load.inspection_load_id);
                }}
              >
                {t('inspectionLoad.assigner.actions.unassign')}
              </Button>
            </LoadListItem>
          ))}
        </List>
      ) : (
        <Instruction>{t('inspectionLoad.assigner.empty.noAssignedLoads')}</Instruction>
      )}
      <Typography variant="h6">{t('inspectionLoad.assigner.sections.unassignedLoads')}</Typography>
      {unassignedLoads?.length ? (
        <List disablePadding>
          {unassignedLoads.map((load) => (
            <LoadListItem key={load.id} load={load}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  if (!load.inspection_load_id) {
                    assign(load.id, inspectionId);
                    return;
                  }
                  reassign(load.inspection_load_id, inspectionId);
                }}
              >
                {t('inspectionLoad.assigner.actions.assign')}
              </Button>
            </LoadListItem>
          ))}
        </List>
      ) : (
        <Instruction>{t('inspectionLoad.assigner.empty.noUnassignedLoads')}</Instruction>
      )}
      <Typography variant="h6">{t('inspectionLoad.assigner.sections.otherInspectionLoads')}</Typography>
      <Typography>{t('inspectionLoad.assigner.sections.otherInspectionLoadsDescription')}</Typography>
      {otherInspectionsLoads?.length ? (
        <List disablePadding>
          {otherInspectionsLoads.map((load) => (
            <LoadListItem key={load.id} load={load}>
              <ConfirmationButton
                size="small"
                variant="secondary"
                onConfirm={() => {
                  if (!load.inspection_load_id) {
                    addError(Error('inspection_load_id missing during reassign'), load);
                    return;
                  }
                  reassign(load.inspection_load_id, inspectionId);
                }}
                title={t('inspectionLoad.assigner.confirmation.title', {
                  values: { inspectionSlug, loadName: load.name },
                })}
                message={t('inspectionLoad.assigner.confirmation.message', {
                  values: { currentInspectionSlug: load.inspection_slug },
                })}
              >
                {t('inspectionLoad.assigner.actions.reassign')}
              </ConfirmationButton>
            </LoadListItem>
          ))}
        </List>
      ) : (
        <Instruction>{t('inspectionLoad.assigner.empty.noOtherInspectionLoads')}</Instruction>
      )}
    </div>
  );
};
